import { ref, computed, watch, onMounted } from 'vue';

import Api from '@/api/Api';
import MapHelpers from '@/components/map/utils/MapHelpers';

import { useSessionStore } from '@/stores/session';
import { useDateRangeAdapter } from '@/utils/useDateRangeAdapter';

import { getBaseOptions, generateSeries, generateDashArray, setXAxisRange } from '@/utils/chartsUtils';
import { addDays, dayJsToString, warningIfNotUtcMidnight, jsNativeDateToDayJs } from '@/components/common/dateUtils';

import { useSubNetworksStore } from '@/stores/subNetworksStore';

const getSeriesKey = item => item.mountpoint + ' - ' + MapHelpers.getGnssDataDisplay(item.gnss).name;
const getValue = item => item.satellites;


export const useGnssVisibilityChartGroup = ({
	props,
	apiMode,
	getMountpointsOptions,
	initMountpointSelectedRef,
}) => {
	const {
		apiData,
		isLoading,
		doRequest: doGnssVisibilityTimeSeriesRequest,
		resetData: resetGnssVisibilityTimeSeriesData,
	} = apiMode === 'baseStation' ?
		Api.useGnssVisibilityBaseStationsTimeSeriesGet() :
		Api.useGnssVisibilityGridpointsTimeSeriesGet();
	const subNetworksStore = useSubNetworksStore();

	const mountpointsOptions = computed(() => getMountpointsOptions());
	const mountpointsSelected = ref(
		(initMountpointSelectedRef != null && initMountpointSelectedRef.value != null) ?
			[initMountpointSelectedRef.value] : []
	);

	const gnssSelectRef = ref();

	const gnssVisibilityTimeSeriesGet = () => {
		if (datesRangeStr.value != null && mountpointsSelected.value.length > 0) {
			const gnssSelectedValue = gnssSelectRef.value != null ? gnssSelectRef.value.selected : [];
			const requestData = {
				start_date: datesRangeStr.value[0],
				end_date: datesRangeStr.value[1],
				mountpoints: mountpointsSelected.value.join(','),
				gnss: gnssSelectedValue.join(','),
			};
			if (apiMode === 'gridpoint') {
				requestData.cnet_id = subNetworksStore.cnetSelectedId;
			}
			doGnssVisibilityTimeSeriesRequest(requestData);
		}
	};

	let datesRange;
	let datesRangeStr;
	let datesRangeSafe;
	if (props.minDate && props.maxDate) {
		warningIfNotUtcMidnight(props.minDate, 'minDate', 'GnssVisibilityChartGroup');
		warningIfNotUtcMidnight(props.maxDate, 'maxDate', 'GnssVisibilityChartGroup');
		const maxDate = addDays(props.maxDate, 1);
		datesRange = ref([jsNativeDateToDayJs(props.minDate), jsNativeDateToDayJs(maxDate)]);
		datesRangeStr = ref(datesRange.value.map(dayJsToString));
		datesRangeSafe = ref([props.minDate.getTime(), maxDate.getTime()]);
	}
	else {
		const sessionStore = useSessionStore();
		const datesAdapter = useDateRangeAdapter(sessionStore, 1, 0);
		datesRange = datesAdapter.datesRange;
		datesRangeStr = datesAdapter.datesRangeStr;
		datesRangeSafe = datesAdapter.datesRangeSafe;
	}

	onMounted(gnssVisibilityTimeSeriesGet);

	if (initMountpointSelectedRef != null) {
		watch(initMountpointSelectedRef, (to, from) => {
			if (from == null && to != null) {
				mountpointsSelected.value = [to];
				gnssVisibilityTimeSeriesGet();
			}
		});
	}

	const onSelectAll = () => {
		mountpointsSelected.value = mountpointsOptions.value.map(item => item.value);
		gnssVisibilityTimeSeriesGet();
	};

	const clearSelectedMountpoints = () => {
		mountpointsSelected.value = [];
		resetGnssVisibilityTimeSeriesData();
	};

	const series = computed(() => generateSeries({
		items: apiData.value.items,
		getSeriesKeyFn: getSeriesKey,
		getValueFn: getValue,
		seriesStep: 5,
		showNullAsZero: true,
	}).series);

	const chartOptions = computed(() => {
		let options = getBaseOptions('gnss_visibility_' + apiMode, series.value);
		setXAxisRange(options, datesRangeSafe.value);
		options.stroke.dashArray = generateDashArray(series.value);
		options.yaxis.title.text = 'Satellites count';
		options.yaxis.decimalsInFloat = 1;
		return options;
	});

	return {
		isLoading,
		mountpointsOptions,
		mountpointsSelected,
		gnssSelectRef,
		onSelectAll,
		clearSelectedMountpoints,
		datesRange,
		series,
		chartOptions,
		gnssVisibilityTimeSeriesGet,
	};
};
